import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Dialog from "../../../designSystem/c-organism/dialogs/Dialog";
import Input from "../../../designSystem/b-molecules/formElements/input/Input";
import Button from "../../../designSystem/b-molecules/buttons/Button";
import { onValue, ref, push, update, remove } from "firebase/database";
import { db } from "../../../configs/Firebase";
import Fuse from "fuse.js";
import style from "./ManageBrands.module.css";
import Icon from "../../../designSystem/a-atoms/icons/Icon";

const ManageSources = ({ show, onClose }) => {
  const org = useSelector((state) => state.orgStatus.value);
  const [sources, setSources] = useState([]);
  const [creatingNew, setCreatingNew] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [newSourceName, setNewSourceName] = useState("");
  const [newSourceKey, setNewSourceKey] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (org.payload && org.payload !== undefined) {
      const sourcesRef = ref(db, `userData/${org.payload}/customers/sources`);

      onValue(sourcesRef, (snapshot) => {
        const data = snapshot.val();
        const temp = [];
        if (data) {
          for (let i in data) {
            temp.push({
              id: i,
              ...data[i],
            });
          }
          setSources(temp);
        } else {
          // Seed with current default feedback types if none exist yet
          const defaultSources = [
            { name: "Customer Service", key: "customer_service" },
            { name: "Google Play", key: "google_play" },
            { name: "App Store", key: "app_store" },
            { name: "Other", key: "other" },
          ];
          defaultSources.forEach((source) => {
            push(sourcesRef, {
              ...source,
              createdAt: { ".sv": "timestamp" },
            });
          });
        }
      });
    }
  }, [org]);

  const fuse = new Fuse(sources, {
    keys: [{ name: "name" }, { name: "key" }],
    includeMatches: true,
  });
  const [sourceResults, setSourceResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  function searchHandler(e) {
    setError("");
    const value = e.target.value;
    setSearchTerm(value);
    if (value) {
      setSourceResults(fuse.search(value));
    } else {
      setSourceResults([]);
    }
  }

  function slugifyKey(val) {
    return val
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  }

  function createNew() {
    if (!newSourceName.trim()) {
      setError("Source name cannot be empty");
      return;
    }

    const key = newSourceKey.trim() || slugifyKey(newSourceName);
    if (!key) {
      setError("Source key cannot be empty");
      return;
    }

    if (
      sources.some(
        (s) =>
          s.name.toLowerCase() === newSourceName.trim().toLowerCase() ||
          (s.key && s.key.toLowerCase() === key.toLowerCase())
      )
    ) {
      setError("A source with this name or key already exists");
      return;
    }

    if (org.payload) {
      const sourcesRef = ref(db, `userData/${org.payload}/customers/sources`);
      const newSource = {
        name: newSourceName.trim(),
        key: key,
        createdAt: { ".sv": "timestamp" },
      };
      push(sourcesRef, newSource);
      setNewSourceName("");
      setNewSourceKey("");
      setCreatingNew(false);
      setError("");
    }
  }

  function startEdit(source) {
    setEditingSource(source);
    setNewSourceName(source.name);
    setNewSourceKey(source.key || "");
    setCreatingNew(false);
    setError("");
  }

  function saveEdit() {
    if (!newSourceName.trim()) {
      setError("Source name cannot be empty");
      return;
    }

    const key = newSourceKey.trim() || slugifyKey(newSourceName);
    if (!key) {
      setError("Source key cannot be empty");
      return;
    }

    if (
      sources.some(
        (s) =>
          s.id !== editingSource.id &&
          (s.name.toLowerCase() === newSourceName.trim().toLowerCase() ||
            (s.key && s.key.toLowerCase() === key.toLowerCase()))
      )
    ) {
      setError("Another source with this name or key already exists");
      return;
    }

    if (org.payload && editingSource) {
      const sourceRef = ref(
        db,
        `userData/${org.payload}/customers/sources/${editingSource.id}`
      );
      update(sourceRef, {
        name: newSourceName.trim(),
        key: key,
      });
      setEditingSource(null);
      setNewSourceName("");
      setNewSourceKey("");
      setError("");
    }
  }

  function deleteSource(sourceId) {
    if (org.payload) {
      const sourceRef = ref(
        db,
        `userData/${org.payload}/customers/sources/${sourceId}`
      );
      remove(sourceRef);
    }
  }

  function cancel() {
    setCreatingNew(false);
    setEditingSource(null);
    setNewSourceName("");
    setNewSourceKey("");
    setError("");
  }

  const displaySources = searchTerm ? sourceResults.map((r) => r.item) : sources;

  return (
    <Dialog show={show} onClose={onClose} title="Manage Sources">
      <div className={style.input}>
        <Input
          label="Search Sources"
          placeholder=""
          onChange={searchHandler}
          value={searchTerm}
          error={error}
        />
      </div>

      {creatingNew || editingSource ? (
        <div className={style.editSection}>
          <Input
            label="Source Name"
            placeholder="Enter source name"
            onChange={(e) => setNewSourceName(e.target.value)}
            value={newSourceName}
            error={error}
          />
          <Input
            label="Source Key"
            placeholder="e.g., customer_service"
            onChange={(e) => setNewSourceKey(e.target.value)}
            value={newSourceKey}
          />
          <div className={style.actions}>
            <Button variants={["text"]} onClick={cancel}>
              Cancel
            </Button>
            <Button
              variants={["primary"]}
              onClick={editingSource ? saveEdit : createNew}
            >
              {editingSource ? "Save" : "Create"}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <ul className={style.brandList}>
            {displaySources.length > 0 ? (
              displaySources.map((source) => (
                <li key={source.id}>
                  <span className={style.brandName}>
                    {source.name}
                    {source.key && (
                      <span className={style.brandKey}> ({source.key})</span>
                    )}
                  </span>
                  <div className={style.brandActions}>
                    <Button
                      variants={["text", "small"]}
                      onClick={() => startEdit(source)}
                    >
                      <Icon>edit</Icon>
                    </Button>
                    <Button
                      variants={["text", "small"]}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Are you sure you want to delete "${source.name}"?`
                          )
                        ) {
                          deleteSource(source.id);
                        }
                      }}
                    >
                      <Icon>delete</Icon>
                    </Button>
                  </div>
                </li>
              ))
            ) : (
              <li className={style.noResults}>
                {searchTerm ? "No sources found" : "No sources created yet"}
              </li>
            )}
          </ul>
          <div className={style.actions}>
            <Button variants={["text"]} onClick={() => setCreatingNew(true)}>
              Create New Source
            </Button>
            <Button variants={["text"]} onClick={onClose}>
              Close
            </Button>
          </div>
        </>
      )}
    </Dialog>
  );
};

export default ManageSources;


